import * as Sentry from "@sentry/react";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    // In production, send events first-party (a Vercel rewrite proxies /relay → Sentry) so ad
    // blockers — which recognize sentry.io — don't drop error reports from those users.
    tunnel: import.meta.env.PROD ? "/relay" : undefined,
    environment: import.meta.env.MODE,
    // Ties each error to the deploy that shipped it. Set VITE_APP_RELEASE (e.g. the git SHA) in Vercel.
    release: import.meta.env.VITE_APP_RELEASE || undefined,
    // We handle PII (emails, addresses, ID/insurance docs). Never attach IP/PII by default, and mask
    // everything in session replays — GDPR-safe.
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // Trace 20% of transactions; only record a replay when something actually breaks.
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      "removeChild",
      "insertBefore",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed",
      "Non-Error promise rejection captured",
    ],
    beforeSend(event) {
      // Suppress DOM mutation errors from browser extensions.
      const msg = event.exception?.values?.[0]?.value ?? "";
      if (msg.includes("removeChild") || msg.includes("insertBefore")) return null;
      return event;
    },
  });
}

/**
 * Identify the current user on all subsequent events (who hit the error).
 * Only the opaque user id is sent — never the email/PII. The id is enough to pivot into our own DB
 * when investigating, and keeps the error tracker free of personal data (GDPR data-minimisation).
 */
export function setSentryUser(user: { id: string }) {
  Sentry.setUser({ id: user.id });
}

/** Tag the user's role so errors can be sliced by audience (customer / technician / admin). */
export function setSentryRole(role: string | null) {
  Sentry.setTag("user_role", role ?? "anonymous");
}

export function clearSentryUser() {
  Sentry.setUser(null);
  Sentry.setTag("user_role", "anonymous");
}

/**
 * Capture a *handled* error with domain context — so money-path / booking / auth failures that would
 * otherwise be `console.error`'d into the void surface in Sentry, grouped by `area` and carrying
 * `context` (booking_id, amount, operation, …).
 */
export function captureError(error: unknown, area: string, context?: Record<string, unknown>) {
  Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
    tags: { area },
    extra: context,
  });
}

/** Add a breadcrumb — a trail leading up to an eventual error (e.g. "started checkout for booking X"). */
export function addBreadcrumb(category: string, message: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({ category, message, level: "info", data });
}

export { Sentry };
